import { FCElement } from '@fc/component';
import { collectCartAddSectionIds, submitCartAdd } from '@fc/cart';
import {
  CartSectionsRenderedEvent,
  FCEvents,
  createCartAddOperationId,
  emitCartAddFailure,
  emitCartAddPending,
  emitCartAddPendingClear,
  emitCartAddSuccess,
  getCartEventData,
  isCartAddSuccessEvent,
} from '@fc/events';
import { cartPerformance } from '@fc/metrics';
import { loadQuickAddModalDependencies } from '@fc/quick-add-modal-assets';
import { QuickAddStatusController } from '@fc/quick-add-status';
import { parsePositiveInt, preserveMarketContext } from '@fc/quick-add-utils';
import { QuickAddVariantResolver } from '@fc/quick-add-variant-resolver';
import { sectionManager } from '@fc/sections';

const QUICK_ADD_MODAL_SECTION_ID = 'quick-add-modal-product';
const QUICK_ADD_MODAL_BODY_SELECTOR = '[data-quick-add-modal-body]';
const QUICK_ADD_CART_DRAWER_RETURN_TIMEOUT_MS = 1600;

export class QuickAddDialogComponent extends FCElement {
  requiredRefs = ['dialogComponent'];
  #requestController = null;
  #activeTrigger = null;
  #cartDrawerReturnTarget = null;
  #pendingCartDrawerReturn = null;
  #cartDrawerReturnTimer = null;
  #isReturningToCartDrawer = false;

  connectedCallback() {
    super.connectedCallback();
    this.addManagedEventListener(this, FCEvents.variantChange, this.#onVariantChange);
    this.addManagedEventListener(this, FCEvents.cartRefresh, this.#onModalCartRefresh);
    this.addManagedEventListener(document, CartSectionsRenderedEvent.eventName, this.#onCartSectionsRendered);
    this.addManagedEventListener(this, 'dialog:close', this.#onDialogClose);
  }

  disconnectedCallback() {
    this.#abortRequest();
    this.#clearCartDrawerReturn();
    super.disconnectedCallback();
  }

  openProduct = async ({ productUrl, productTitle = '', optionValueIds = [], trigger = null } = {}) => {
    const dialogComponent = this.refs.dialogComponent;
    const content = this.#contentElement();
    if (!dialogComponent || !(content instanceof HTMLElement) || !productUrl) return;

    this.#abortRequest();
    this.#setActiveTrigger(trigger);
    this.#requestController = new AbortController();
    const { signal } = this.#requestController;

    dialogComponent.showDialog({ reason: 'quick-add', productUrl });
    this.#resetAccessibleName();
    this.#renderLoading(productTitle);

    try {
      const url = this.#buildProductUrl(productUrl, optionValueIds);
      const [doc] = await Promise.all([
        sectionManager.getSectionDocument(QUICK_ADD_MODAL_SECTION_ID, {
          cache: false,
          signal,
          url,
        }),
        loadQuickAddModalDependencies(),
      ]);

      if (signal.aborted) return;

      this.#replaceContent(doc, productUrl);
    } catch (error) {
      if (signal.aborted) return;
      this.#renderError(productUrl);
    } finally {
      if (this.#requestController?.signal === signal) {
        this.#requestController = null;
      }
    }
  };

  #buildProductUrl(productUrl, optionValueIds = []) {
    const url = preserveMarketContext(new URL(productUrl, window.location.origin));
    url.searchParams.delete('variant');

    const ids = Array.isArray(optionValueIds) ? optionValueIds.filter(Boolean) : [];
    if (ids.length) {
      url.searchParams.set('option_values', ids.join(','));
    } else {
      url.searchParams.delete('option_values');
    }

    return url;
  }

  #replaceContent(doc, productUrl) {
    const content = this.#contentElement();
    if (!(content instanceof HTMLElement)) return;

    const sectionRoot = doc?.getElementById?.(`shopify-section-${QUICK_ADD_MODAL_SECTION_ID}`) ?? doc;
    const nextBody = sectionRoot?.querySelector?.(QUICK_ADD_MODAL_BODY_SELECTOR);
    if (!(nextBody instanceof HTMLElement)) {
      this.#renderError(productUrl);
      return;
    }

    content.replaceChildren(document.importNode(nextBody, true));
    window.FitCheck?.loadProductFormUI?.();
    window.FitCheck?.loadVariantPicker?.();
    this.#syncAccessibleName();
    this.#syncVariantState();

    requestAnimationFrame(() => {
      const title = this.querySelector('[data-quick-add-modal-title]');
      if (title instanceof HTMLElement) {
        title.focus({ preventScroll: true });
      }
    });
  }

  #syncAccessibleName() {
    const dialog = this.refs.dialogComponent?.refs?.dialog;
    const title = this.querySelector('[data-quick-add-modal-title]');
    if (!(dialog instanceof HTMLDialogElement) || !(title instanceof HTMLElement) || !title.id) return;

    dialog.setAttribute('aria-labelledby', title.id);
    dialog.removeAttribute('aria-label');
  }

  #resetAccessibleName() {
    const dialog = this.refs.dialogComponent?.refs?.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    dialog.removeAttribute('aria-labelledby');
    dialog.setAttribute('aria-label', dialog.dataset.defaultLabel || 'Quick add');
  }

  #renderLoading(productTitle = '') {
    const content = this.#contentElement();
    if (!(content instanceof HTMLElement)) return;

    const loading = document.createElement('div');
    loading.className = 'quick-add-modal__loading';
    loading.setAttribute('role', 'status');
    loading.setAttribute('aria-live', 'polite');
    loading.textContent = window.FitCheck?.translations?.loading || 'Loading...';
    if (productTitle) {
      loading.setAttribute('aria-label', `${loading.textContent} ${productTitle}`);
    }

    content.replaceChildren(loading);
  }

  #renderError(productUrl = '') {
    const content = this.#contentElement();
    if (!(content instanceof HTMLElement)) return;

    const error = document.createElement('div');
    error.className = 'quick-add-modal__error';
    error.setAttribute('role', 'alert');

    const message = document.createElement('p');
    message.textContent = window.FitCheck?.translations?.quick_add_unavailable || 'Unable to load purchase options.';
    error.append(message);

    if (productUrl) {
      const link = document.createElement('a');
      link.href = new URL(productUrl, window.location.origin).href;
      link.textContent = window.FitCheck?.translations?.view_more_details || 'More details';
      error.append(link);
    }

    content.replaceChildren(error);
  }

  #contentElement() {
    return this.querySelector('#quick-add-modal-content');
  }

  #setActiveTrigger(trigger) {
    if (this.#activeTrigger instanceof HTMLElement && this.#activeTrigger !== trigger) {
      this.#activeTrigger.setAttribute('aria-expanded', 'false');
    }

    this.#activeTrigger = trigger instanceof HTMLElement ? trigger : null;
    this.#cartDrawerReturnTarget = this.#resolveCartDrawerReturnTarget(this.#activeTrigger);
    this.#activeTrigger?.setAttribute('aria-expanded', 'true');
  }

  #resolveCartDrawerReturnTarget(trigger) {
    if (!(trigger instanceof HTMLElement)) return null;
    if (!trigger.closest('.cart-drawer-upsells')) return null;

    const drawer = trigger.closest('cart-drawer');
    return drawer instanceof HTMLElement ? drawer : null;
  }

  #onVariantChange = (event) => {
    this.#syncVariantState(event.detail?.resource ?? null);
  };

  #onModalCartRefresh = (event) => {
    if (!this.#cartDrawerReturnTarget) return;
    if (!isCartAddSuccessEvent(event)) return;

    const data = getCartEventData(event);
    this.#pendingCartDrawerReturn = {
      operationId: data.cartAddOperationId || null,
      sourceId: event.detail?.sourceId || null,
    };
    this.#scheduleCartDrawerReturnFallback();
  };

  #onCartSectionsRendered = (event) => {
    if (!this.#pendingCartDrawerReturn) return;
    if (!this.#matchesCartDrawerReturn(event)) return;

    void this.#finishCartDrawerReturn('cart-sections-rendered');
  };

  #matchesCartDrawerReturn(event) {
    const pending = this.#pendingCartDrawerReturn;
    if (!pending) return false;

    const data = getCartEventData(event);
    if (pending.operationId && data.cartAddOperationId) {
      return pending.operationId === data.cartAddOperationId;
    }

    const sourceId = event.detail?.sourceId || null;
    return !pending.sourceId || !sourceId || pending.sourceId === sourceId;
  }

  #scheduleCartDrawerReturnFallback() {
    window.clearTimeout(this.#cartDrawerReturnTimer);
    this.#cartDrawerReturnTimer = window.setTimeout(() => {
      this.#cartDrawerReturnTimer = null;
      if (this.#pendingCartDrawerReturn) {
        void this.#finishCartDrawerReturn('cart-sections-timeout');
      }
    }, QUICK_ADD_CART_DRAWER_RETURN_TIMEOUT_MS);
  }

  async #finishCartDrawerReturn(reason) {
    const drawer = this.#cartDrawerReturnTarget;
    this.#clearCartDrawerReturn({ keepTarget: true });
    if (!(drawer instanceof HTMLElement) || !drawer.isConnected) return;

    const dialogComponent = this.refs.dialogComponent;
    if (dialogComponent?.refs?.dialog?.open && typeof dialogComponent.closeDialog === 'function') {
      this.#isReturningToCartDrawer = true;
      try {
        await dialogComponent.closeDialog({ restoreFocus: false, reason: `quick-add-${reason}` });
      } finally {
        this.#isReturningToCartDrawer = false;
      }
    }

    this.#openCartDrawerReturn(drawer);
    this.#cartDrawerReturnTarget = null;
  }

  #openCartDrawerReturn(drawer) {
    requestAnimationFrame(() => {
      if (typeof drawer.open === 'function') {
        drawer.open();
      } else if (typeof drawer.showDialog === 'function') {
        drawer.showDialog({ reason: 'quick-add-return' });
      }

      requestAnimationFrame(() => {
        const focusTarget = drawer.querySelector('[data-dialog-initial-focus]');
        if (focusTarget instanceof HTMLElement) {
          focusTarget.focus({ preventScroll: true });
        }
      });
    });
  }

  #clearCartDrawerReturn({ keepTarget = false } = {}) {
    window.clearTimeout(this.#cartDrawerReturnTimer);
    this.#cartDrawerReturnTimer = null;
    this.#pendingCartDrawerReturn = null;
    if (!keepTarget) {
      this.#cartDrawerReturnTarget = null;
    }
  }

  #syncVariantState(resource) {
    const body = this.querySelector(QUICK_ADD_MODAL_BODY_SELECTOR);
    if (!(body instanceof HTMLElement)) return;

    const state = this.#resolveVariantState(body, resource);
    body.dataset.selectedVariantState = state;
    if (resource?.id) {
      body.dataset.selectedVariantId = String(resource.id);
    } else if (state !== 'selected') {
      body.dataset.selectedVariantId = '';
    }

    const status = body.querySelector('[data-quick-add-selection-status]');
    if (status instanceof HTMLElement) {
      const label = this.#statusLabel(body, state);
      status.textContent = label;
      status.toggleAttribute('hidden', !label);
    }

    this.#setPurchaseDisabled(state !== 'selected');
  }

  #resolveVariantState(body, resource) {
    if (resource?.available === true) return 'selected';
    if (resource?.available === false) return 'sold_out';
    if (resource === undefined) return body.dataset.selectedVariantState || 'unresolved';

    const picker = body.querySelector('variant-select');
    const optionCount = parsePositiveInt(body.dataset.optionCount);
    const selectedOptionCount =
      Array.isArray(picker?.selectedOptionsValues) && picker.selectedOptionsValues.length
        ? picker.selectedOptionsValues.length
        : parsePositiveInt(body.dataset.selectedOptionCount);

    if (optionCount > 0 && selectedOptionCount < optionCount) return 'unresolved';
    if (resource === null) return 'nonexistent';

    return body.dataset.selectedVariantState || 'unresolved';
  }

  #statusLabel(body, state) {
    if (state === 'selected') return '';
    if (state === 'sold_out') return body.dataset.fitSoldOutLabel || 'Out of stock';
    if (state === 'unresolved') return body.dataset.fitUnresolvedLabel || 'Choose available options';
    return body.dataset.fitUnavailableLabel || 'Unavailable';
  }

  #setPurchaseDisabled(disabled) {
    if (!disabled) return;

    this.querySelectorAll('[data-ref="addToCartButton"], qty-selector button, qty-selector input').forEach((control) => {
      if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      }
    });

    this.querySelectorAll('input[name="id"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = '';
    });
  }

  #onDialogClose = () => {
    const drawer = this.#cartDrawerReturnTarget;
    const shouldReturnToCartDrawer =
      !this.#isReturningToCartDrawer &&
      !this.#pendingCartDrawerReturn &&
      drawer instanceof HTMLElement &&
      drawer.isConnected;

    this.#abortRequest();
    if (this.#pendingCartDrawerReturn) {
      this.#clearCartDrawerReturn();
    }
    this.#activeTrigger?.setAttribute('aria-expanded', 'false');
    this.#activeTrigger = null;

    if (shouldReturnToCartDrawer) {
      this.#cartDrawerReturnTarget = null;
      this.#openCartDrawerReturn(drawer);
    }
  };

  #abortRequest() {
    this.#requestController?.abort();
    this.#requestController = null;
  }
}

export class QuickAddComponent extends FCElement {
  #status = new QuickAddStatusController(this);
  #variants = new QuickAddVariantResolver(this);

  connectedCallback() {
    super.connectedCallback();

    this.addManagedEventListener(document, 'click', this.#onOutsideClick, { capture: true });
    this.addManagedEventListener(this, 'click', this.#onPlusClick, { capture: true });
    this.addManagedEventListener(this, 'click', this.#onSizeClick, { capture: true });
    this.addManagedEventListener(this, 'keydown', this.#onKeyDown);
    this.addManagedEventListener(this, 'submit', this.#onSubmit, { capture: true });
    this.addManagedEventListener(this, 'pointerover', this.#onPrewarmIntent, { capture: true, passive: true });
    this.addManagedEventListener(this, 'focusin', this.#onPrewarmIntent, { capture: true });

    if (this.dataset.hoverTrigger === 'true') {
      const plus = this.querySelector('.quick-add__button--plus');
      if (plus) {
        this.addManagedEventListener(plus, 'mouseenter', this.#onHoverIn);
        this.addManagedEventListener(this, 'mouseleave', this.#onHoverOut);
      }
    }
  }

  disconnectedCallback() {
    this.#status.destroy();
    super.disconnectedCallback();
  }

  #sizePanel = () => this.querySelector('.quick-add__size-selector');
  #plusButton = () => this.querySelector('.quick-add__button--plus');
  #usesModal = () => this.dataset.quickAddMode === 'modal';
  #isCartDrawerOpen = () => document.querySelector('cart-drawer dialog[open]') instanceof HTMLElement;
  #isCartOwnedForm = (form) =>
    form instanceof HTMLElement &&
    Boolean(form.closest('cart-drawer, cart-items, .cart-drawer-upsells, .cart-drawer__dialog'));
  #shouldUseFastConfirmedAdd = (form) =>
    form instanceof HTMLFormElement && !this.#isCartOwnedForm(form) && !this.#isCartDrawerOpen();

  #showPanel({ focusPanel = false } = {}) {
    const panel = this.#sizePanel();
    if (!panel) return;
    this.#variants.refreshAvailability(panel);

    const hasResolvableButton = panel.querySelector('.quick-add__size-button:not(:disabled)');
    if (!hasResolvableButton) {
      this.#openModal(this.#plusButton());
      return;
    }

    panel.removeAttribute('hidden');
    panel.removeAttribute('inert');
    panel.setAttribute('aria-hidden', 'false');
    this.#plusButton()?.setAttribute('aria-expanded', 'true');

    if (focusPanel) {
      const firstAvailableButton = panel.querySelector('.quick-add__size-button:not(:disabled)');
      firstAvailableButton?.focus();
    }
  }

  #hidePanel({ returnFocus = false } = {}) {
    const panel = this.#sizePanel();
    if (!panel) return;

    const focusWasInsidePanel = panel.contains(document.activeElement);
    panel.setAttribute('hidden', '');
    panel.setAttribute('inert', '');
    panel.setAttribute('aria-hidden', 'true');
    this.#plusButton()?.setAttribute('aria-expanded', 'false');

    if (returnFocus || focusWasInsidePanel) {
      this.#plusButton()?.focus();
    }
  }

  #onHoverIn = () => this.#showPanel();

  #onHoverOut = () => this.#hidePanel();

  #onPrewarmIntent = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('.quick-add__size-button');
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;

    this.#variants.prewarmButton(button);
  };

  #onPlusClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const modalTrigger = target?.closest('[data-quick-add-modal-trigger]');
    if (modalTrigger && this.contains(modalTrigger)) {
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
      this.#openModal(modalTrigger);
      return false;
    }

    const directAddButton = target?.closest('.quick-add__button--add');
    if (directAddButton && this.contains(directAddButton)) {
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
      void this.#submitDirectAddButton(directAddButton, event);
      return false;
    }

    const plus = target?.closest('.quick-add__button--plus');
    if (!plus) return;
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();

    if (this.#usesModal()) {
      this.#openModal(plus);
      return false;
    }

    this.toggleSizeSelector(event);
    return false;
  };

  #onSizeClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const btn = target?.closest('.quick-add__size-button');
    if (!btn) return;
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();
    this.#cartFromButton(btn);
    return false;
  };

  #onOutsideClick = (event) => {
    const panel = this.#sizePanel();
    if (!panel || panel.hasAttribute('hidden')) return;

    const plus = this.querySelector('.quick-add__button--plus');
    if (plus?.contains(event.target)) return;
    if (!this.contains(event.target)) this.#hidePanel();
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    const panel = this.#sizePanel();
    if (!panel || panel.hasAttribute('hidden')) return;

    event.stopPropagation();
    event.preventDefault();
    this.#hidePanel({ returnFocus: true });
  };

  #openModal(trigger = null, optionValueIds = null) {
    const dialog = document.getElementById('quick-add-dialog');
    if (!dialog || typeof dialog.openProduct !== 'function') return;

    const source = trigger instanceof HTMLElement ? trigger : null;
    const { productUrl } = this.#variants.selectedCardOptionState(source);
    const ids = Array.isArray(optionValueIds) ? optionValueIds : this.#variants.selectedOptionValueIds(source);
    this.#hidePanel();
    this.#status.clearError();
    this.#status.clearSuccess();

    dialog.openProduct({
      productUrl: productUrl || this.dataset.productUrl || '',
      productTitle: this.dataset.productTitle || '',
      optionValueIds: ids,
      trigger: source,
    });
  }

  toggleSizeSelector = (event) => {
    if (!event) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    const panel = this.#sizePanel();
    if (!panel) return;
    const focusPanel = event instanceof KeyboardEvent || event.detail === 0;

    if (panel.hasAttribute('hidden')) {
      this.#showPanel({ focusPanel });
    } else {
      this.#hidePanel({ returnFocus: focusPanel });
    }
  };

  selectSize = async (event) => {
    if (!event) return;
    event.preventDefault?.();
    event.stopImmediatePropagation?.();
    if (event.target) await this.#cartFromButton(event.target);
  };

  selectOption = async (event) => {
    await this.selectSize(event);
  };

  #cartFromButton = async (button) => {
    const optionValue = button.dataset.optionValue || button.dataset.sizeValue;
    if (!optionValue) return;

    let info = null;
    try {
      info = await this.#variants.resolveVariantFromButton(button);
    } catch {
      info = null;
    }
    if (!info?.available) {
      this.#openModal(button, this.#variants.selectedOptionValueIds(button));
      return;
    }

    const { id: variantId } = info;
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    const idInput = form.querySelector('input[name="id"]');
    if (idInput) idInput.value = String(variantId);

    // Mark selected option visually.
    const panel = this.#sizePanel();
    if (panel) {
      panel.querySelectorAll('.quick-add__size-button').forEach((b) => {
        b.classList.remove('quick-add__size-button--selected');
      });
      button.classList.add('quick-add__size-button--selected');
    }

    this.#hidePanel({ returnFocus: true });
    await this.#postToCart(form, String(variantId));
  };

  async #submitDirectAddButton(button, event) {
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const form = button.closest('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;

    const idInput = form.querySelector('input[name="id"]');
    const variantId = idInput?.value;
    if (!variantId) return;

    await this.#postToCart(
      form,
      variantId,
      cartPerformance.createStartingMarker('add:user-action', event),
      createCartAddOperationId('quick-add'),
    );
  }

  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[data-type="add-to-cart-form"]')) return;

    event.preventDefault();
    event.stopPropagation();

    const idInput = form.querySelector('input[name="id"]');
    const variantId = idInput?.value;
    if (!variantId) return;

    await this.#postToCart(
      form,
      variantId,
      cartPerformance.createStartingMarker('add:user-action', event),
      createCartAddOperationId('quick-add'),
    );
  };

  async #postToCart(form, variantId, addUserActionMarker = null, cartAddOperationId = createCartAddOperationId('quick-add')) {
    if (this.classList.contains('quick-add--loading')) return;

    const formId = form.getAttribute('id') || '';
    const isCartDrawerUpsell = Boolean(form.closest('.cart-drawer-upsells'));
    const useFastConfirmedAdd = this.#shouldUseFastConfirmedAdd(form);
    const sectionIds = useFastConfirmedAdd ? [] : collectCartAddSectionIds(document, { sourceElement: form });
    const requestedQuantity = Number.parseInt(String(new FormData(form).get('quantity') ?? '1'), 10);
    const pendingEventData = cartPerformance.withUserActionMarker(
      {
        action: 'add',
        backgroundCartRefresh: useFastConfirmedAdd,
        cartAddOperationId,
        suppressCartAutoOpen: useFastConfirmedAdd,
        source: 'quick-add',
        productId: this.dataset.productId || null,
        quantityRequested: Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : 1,
        variantId,
      },
      addUserActionMarker,
    );
    let deferAddUserActionMetric = false;
    const withFastAddContext = (data = {}) => ({
      ...data,
      backgroundCartRefresh: useFastConfirmedAdd,
      suppressCartAutoOpen: useFastConfirmedAdd,
    });

    this.classList.add('quick-add--loading');
    this.setAttribute('aria-busy', 'true');
    this.#status.clearError();
    this.#status.clearSuccess();
    this.#status.showPending();
    emitCartAddPending(this, formId, pendingEventData);

    try {
      const result = await submitCartAdd(form, {
        signal: this.lifecycleSignal,
        source: 'quick-add',
        productId: this.dataset.productId,
        quantityDefault: 1,
        variantId,
        cartAddOperationId,
        sectionIds,
        optimisticCount: !isCartDrawerUpsell,
      });
      if (result.aborted) {
        this.#status.clearSuccess();
        emitCartAddPendingClear(
          this,
          formId,
          cartPerformance.withUserActionMarker(
            withFastAddContext(result.eventData ?? { ...pendingEventData, aborted: true }),
            addUserActionMarker,
          ),
        );
        return;
      }

      if (!result.ok) {
        emitCartAddFailure(
          this,
          formId,
          result.error,
          cartPerformance.withUserActionMarker(withFastAddContext(result.eventData), addUserActionMarker),
        );
        this.#status.showError(result.error);
        return;
      }

      this.#status.clearError();
      this.#status.showSuccess();
      deferAddUserActionMetric = true;
      emitCartAddSuccess(
        this,
        formId,
        result.payload,
        cartPerformance.withUserActionMarker(withFastAddContext(result.eventData), addUserActionMarker),
      );
    } catch (err) {
      const error = {
        message: 'Unable to add this item to your cart.',
        description: err instanceof Error ? err.message : 'Unknown error',
        errors: {},
      };

      emitCartAddFailure(
        this,
        formId,
        error,
        cartPerformance.withUserActionMarker(
          {
            action: 'add',
            backgroundCartRefresh: useFastConfirmedAdd,
            suppressCartAutoOpen: useFastConfirmedAdd,
            source: 'quick-add',
            productId: this.dataset.productId || null,
            variantId,
          },
          addUserActionMarker,
        ),
      );
      this.#status.showError(error);
    } finally {
      this.classList.remove('quick-add--loading');
      this.removeAttribute('aria-busy');
      if (deferAddUserActionMetric) {
        cartPerformance.deferMeasureFromMarker(addUserActionMarker);
      } else {
        cartPerformance.measureFromMarker(addUserActionMarker);
      }
    }
  }

}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialogComponent);
}

if (!customElements.get('quick-add')) {
  customElements.define('quick-add', QuickAddComponent);
}
